import { usePrintInvoice } from 'api-hooks/general/mutation';
import { AuthorizationRules } from 'common/constants';
import { base64ToUrl } from 'common/utils/string';
import notification from 'components/common/notification';
import FileSaver from 'file-saver';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

export default function useSalesSaveInvoice(type, data) {
  const { t } = useTranslation();
  const { can } = useAuthorization();
  const { mutateAsync } = usePrintInvoice();
  const dialog = useDialog();
  const id = data.id;
  const invoiceNumber = data.invoiceNumber;

  const permission = React.useMemo(() => {
    switch (type) {
      case 'flight-sales':
        return AuthorizationRules.FlightSalesPrintPdf;
      case 'hotel-sales':
        return AuthorizationRules.HotelSalesPrintPdf;
      case 'cruise-sales':
        return AuthorizationRules.CruiseSalesPrintPdf;
      case 'document-sales':
        return AuthorizationRules.DocumentSalesPrintPdf;
      case 'activity-sales':
        return AuthorizationRules.ActivitySalesPrintPdf;
      case 'product-sales':
        return AuthorizationRules.ProductSalesPrintPdf;
      case 'tour-sales':
        return AuthorizationRules.TourSalesPrintPdf;
      default:
        throw new Error(`Invalid type: ${type}`);
    }
  }, [type]);

  const saveInvoicePermission = can(permission);

  const onDownloadInvoice = React.useCallback(
    (result) => {
      const fileName = `Invoice_${invoiceNumber}`;
      FileSaver.saveAs(result as Blob, fileName);
    },
    [invoiceNumber],
  );

  const onPrintInvoice = React.useCallback((result) => {
    const base64string = result.split(',')[1];
    const url = base64ToUrl(base64string, 'application/pdf');

    const printWindow = window.open(url, '_blank');
    printWindow?.print();
  }, []);

  const showPrintOrDownloadConfirmation = React.useCallback(
    (result) => {
      dialog.showConfirmation({
        message: t('invoice:print_or_download_confirmation_text'),
        title: t('common:confirmation'),
        positiveLabel: t('common:print'),
        onPositiveAction: (onClose) => {
          try {
            onPrintInvoice(result);
          } catch (error) {
            notification.error({ message: error.message });
          } finally {
            onClose();
          }
        },
        negativeLabel: t('common:download'),
        onNegativeAction: (onClose) => {
          try {
            onDownloadInvoice(result);
          } catch (error) {
            notification.error({ message: error.message });
          } finally {
            onClose();
          }
        },
      });
    },
    [dialog, onDownloadInvoice, onPrintInvoice, t],
  );

  const onSaveInvoice = React.useCallback(() => {
    if (saveInvoicePermission) {
      dialog.showConfirmation({
        message: t('invoice:invoice_confirmation_text'),
        title: t('common:confirmation'),
        onPositiveAction: async (onClose) => {
          try {
            const result = await mutateAsync({
              id,
              type,
              body: {
                showDate: true,
              },
            });
            showPrintOrDownloadConfirmation(result);
          } catch (error) {
            notification.error({ message: error.message });
          }
        },
        onNegativeAction: async (onClose) => {
          try {
            const result = await mutateAsync({
              id,
              type,
              body: {
                showDate: false,
              },
            });
            showPrintOrDownloadConfirmation(result);
          } catch (error) {
            notification.error({ message: error.message });
          }
        },
      });
    } else {
      notification.error({
        message: t('common:unauthorized_action'),
      });
    }
  }, [
    dialog,
    id,
    mutateAsync,
    saveInvoicePermission,
    showPrintOrDownloadConfirmation,
    t,
    type,
  ]);

  return {
    saveInvoicePermission,
    onSaveInvoice,
  };
}
